import React from "react";
import classnames from "classnames";
import { useSnapshot } from "valtio";
import { useTranslation } from "react-i18next/";
import { StyledButton } from "@aureskonnect/react-ui";

import {
    store,
    setMonetics,
    setSelectedPeripherals,
    setCheckModificationEffect,
} from "./store";
import { store as useStore } from "../PeripheralConfigure/MoneticPeripheralConfigure/store";
import { getAuthorizationStates } from "@components/VerticalLayout/StyledSidebarMenu";
import { store as globalStore } from "@components/VerticalLayout/store";

import "./index.css";
import { useSelector } from "react-redux";
import { ID_MONETICS } from "@constants/menu";
import { PERIPHERAL_TYPE_MONETIC } from "@constants/index";

type SubRow = {
    peripheralType: string;
    peripheralId: string;
    id_monetic?: string;
    shopId?: string;
    subRows?: SubRow[];
    [key: string]: unknown;
};

type ApplicationRow = {
    shopId: string;
    subRows?: SubRow[];
    appId?: string;
    [key: string]: unknown;
};

type PeripheralElement = {
    Id: string;
    [key: string]: unknown;
};

type DataTableEntry = {
    shopId: string;
    subRows?: SubRow[];
    [key: string]: unknown;
};

type RootState = {
    global: {
        infoUser: {
            profileAuthorization: Record<string, unknown>;
        };
    };
};

type MoneticsActionColumnProps = {
    rowData: ApplicationRow;
    element: PeripheralElement;
    selectedRows: ApplicationRow[];
};

export default function MoneticsActionColumn({
    rowData,
    element,
    selectedRows,
}: MoneticsActionColumnProps) {
    const { t } = useTranslation();
    const { monetics } = useSnapshot(store);
    const { dataTable } = useSnapshot(useStore);
    const { oneShop } = useSnapshot(globalStore);
    const authorizationList = useSelector(
        (state: RootState) => state.global.infoUser.profileAuthorization
    );
    const modify = getAuthorizationStates(ID_MONETICS, authorizationList)
        .Modifie;
    const [isSelected, setIsSelected] = React.useState<boolean>(false);

    const moneticIds = React.useMemo(
        () => new Set(monetics.map((item: PeripheralElement) => item.Id)),
        [monetics]
    );
    const isCurrentSelected = moneticIds.has(element.Id);
    const validSelectedRows = React.useMemo(
        () =>
            selectedRows.filter(
                (elt: ApplicationRow) => elt[t("Application")] !== ""
            ),
        [selectedRows, t]
    );

    const alreadyAssociatedIds = React.useMemo(() => {
        const ids = new Set<string>();
        if (monetics.length > 0) return ids;

        const targetShopId =
            validSelectedRows.length === 1
                ? validSelectedRows[0].shopId
                : rowData.shopId;

        const collectMonetics = (rows: SubRow[]) => {
            for (const el of rows) {
                if (oneShop && el.peripheralType === PERIPHERAL_TYPE_MONETIC) {
                    if (!moneticIds.has(el.id_monetic ?? "")) {
                        ids.add(el.id_monetic ?? "");
                    }
                } else if (!oneShop && el?.subRows) {
                    for (const row of el.subRows) {
                        if (
                            row.peripheralType === PERIPHERAL_TYPE_MONETIC &&
                            !moneticIds.has(row.id_monetic ?? "")
                        ) {
                            ids.add(row.id_monetic ?? "");
                        }
                    }
                }
            }
        };

        (dataTable as DataTableEntry[]).forEach((entry: DataTableEntry) => {
            if (entry.shopId === targetShopId && entry.subRows) {
                collectMonetics(entry.subRows);
            }
        });

        return ids;
    }, [
        validSelectedRows,
        rowData.shopId,
        monetics,
        oneShop,
        moneticIds,
        dataTable,
    ]);
    const isAlreadyAssociated = alreadyAssociatedIds.has(element.Id);

    const sourceSubRows: SubRow[] | undefined =
        validSelectedRows.length > 0
            ? validSelectedRows[0].subRows
            : rowData.subRows;
    const currentAssociatedId = sourceSubRows?.find(
        (el: SubRow) => el.peripheralType === PERIPHERAL_TYPE_MONETIC
    )?.id_monetic;
    const isNotCurrentAssociated = currentAssociatedId !== element.Id;

    const isDisabled =
        (((!isCurrentSelected && monetics.length === 1) ||
            isAlreadyAssociated) &&
            ((isNotCurrentAssociated && monetics.length === 0) ||
                monetics.length !== 0)) ||
        modify === false ||
        validSelectedRows.length > 1;

    return (
        <StyledButton
            rounded
            outline
            className={`${classnames(
                "button-style-select___clz afa_btn_selectPeripheral cmn_icn_dissociate",
                { "not-allowed-icon__clz": isDisabled }
            )}`}
            variant={isCurrentSelected ? "danger" : "light"}
            onClick={() => {
                setMonetics(element);
                setIsSelected(!isSelected);
                setSelectedPeripherals(element);
                setCheckModificationEffect(true);
            }}
        >
            <span>{isCurrentSelected ? t("Deselect") : t("Select")}</span>
        </StyledButton>
    );
}
